import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function Dashboard({ role, staffId }) {
  const [stats, setStats] = useState({
    todayAppointments: 0,
    totalClients: 0,
    weekRevenue: 0,
    pendingCheckouts: 0,
  })
  const [todayAppointments, setTodayAppointments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboard()
  }, [])

  async function fetchDashboard() {
    const today = new Date().toISOString().split('T')[0]
    const weekAgo = new Date()
    weekAgo.setDate(weekAgo.getDate() - 7)
    const weekAgoStr = weekAgo.toISOString().split('T')[0]

    let aptQuery = supabase
      .from('appointments')
      .select(`*, services (name, price), profiles!appointments_customer_id_fkey (full_name), staff (full_name)`)
      .eq('appointment_date', today)
      .order('start_time')

    if (role === 'provider' && staffId) {
      aptQuery = aptQuery.eq('staff_id', staffId)
    }

    const [aptRes, clientsRes, revenueRes] = await Promise.all([
      aptQuery,
      supabase.from('profiles').select('id', { count: 'exact' }).eq('role', 'customer'),
      supabase.from('appointments')
        .select('total_price')
        .gte('appointment_date', weekAgoStr)
        .eq('status', 'completed'),
    ])

    const appointments = aptRes.data || []
    setTodayAppointments(appointments)

    const weekRevenue = (revenueRes.data || [])
      .reduce((sum, a) => sum + parseFloat(a.total_price || 0), 0)

    setStats({
      todayAppointments: appointments.length,
      totalClients: clientsRes.count || 0,
      weekRevenue: weekRevenue.toFixed(0),
      pendingCheckouts: appointments.filter(a => a.status === 'confirmed').length,
    })

    setLoading(false)
  }

  function getStatusColor(status) {
    switch (status) {
      case 'confirmed': return '#D4AF77'
      case 'completed': return '#4A754A'
      case 'cancelled': return '#ef4444'
      default: return '#888'
    }
  }

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  if (loading) return <div style={styles.loading}>Loading...</div>

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.greeting}>{greeting}</h1>
        <p style={styles.date}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.todayAppointments}</div>
          <div style={styles.statLabel}>Today's Appointments</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.pendingCheckouts}</div>
          <div style={styles.statLabel}>Pending Checkouts</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#D4AF77' }}>${stats.weekRevenue}</div>
          <div style={styles.statLabel}>This Week's Revenue</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.totalClients}</div>
          <div style={styles.statLabel}>Total Clients</div>
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>TODAY'S SCHEDULE</h2>
        {todayAppointments.length === 0 ? (
          <div style={styles.empty}>No appointments today</div>
        ) : (
          <div style={styles.appointmentList}>
            {todayAppointments.map(apt => (
              <div key={apt.id} style={styles.aptCard}>
                <div style={styles.aptTime}>{apt.start_time}</div>
                <div style={styles.aptInfo}>
                  <div style={styles.aptClient}>{apt.profiles?.full_name}</div>
                  <div style={styles.aptService}>{apt.services?.name}</div>
                  {role !== 'provider' && (
                    <div style={styles.aptStaff}>✂️ {apt.staff?.full_name}</div>
                  )}
                </div>
                <div style={styles.aptRight}>
                  <div style={styles.aptPrice}>${apt.services?.price}</div>
                  <div style={{
                    ...styles.aptStatus,
                    color: getStatusColor(apt.status),
                    backgroundColor: getStatusColor(apt.status) + '20',
                  }}>
                    {apt.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  container: {
    padding: '32px',
    maxWidth: '1200px',
  },
  loading: {
    padding: '32px',
    color: '#555',
  },
  header: {
    marginBottom: '32px',
    borderBottom: '1px solid #1a1a1a',
    paddingBottom: '24px',
  },
  greeting: {
    fontSize: '32px',
    fontWeight: '300',
    color: '#F5F0E8',
    fontFamily: 'Cormorant Garamond, serif',
    letterSpacing: '1px',
    marginBottom: '4px',
  },
  date: {
    fontSize: '12px',
    color: '#555',
    letterSpacing: '1px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '32px',
  },
  statCard: {
    backgroundColor: '#111',
    border: '1px solid #1a1a1a',
    borderRadius: '2px',
    padding: '20px',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '300',
    color: '#F5F0E8',
    fontFamily: 'Cormorant Garamond, serif',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '10px',
    color: '#555',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '10px',
    fontWeight: '600',
    color: '#555',
    letterSpacing: '2px',
    marginBottom: '16px',
  },
  empty: {
    color: '#444',
    fontSize: '14px',
    padding: '24px',
    backgroundColor: '#111',
    border: '1px solid #1a1a1a',
    borderRadius: '2px',
    textAlign: 'center',
  },
  appointmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  aptCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    backgroundColor: '#111',
    border: '1px solid #1a1a1a',
    borderRadius: '2px',
    padding: '16px 20px',
  },
  aptTime: {
    fontSize: '13px',
    color: '#D4AF77',
    fontWeight: '600',
    letterSpacing: '0.5px',
    width: '80px',
    flexShrink: 0,
  },
  aptInfo: {
    flex: 1,
  },
  aptClient: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#F5F0E8',
    marginBottom: '2px',
  },
  aptService: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '2px',
  },
  aptStaff: {
    fontSize: '12px',
    color: '#444',
  },
  aptRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '6px',
  },
  aptPrice: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#D4AF77',
  },
  aptStatus: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '3px 8px',
    borderRadius: '2px',
  },
}